import { v4 as uuid } from 'uuid';
import { getUserInfoPIIClient } from 'MMT-UI/getPIIData/client';
import { getAuthToken, getMMT_HLD_OBJ } from '../client/common/utils/appUtils';
import { PAGE_NAMES } from './analytics-constants';
import { MMT_POKUS_VAR } from '../client/common/constants/mmtGlobal';
import {
  getSessionStorage,
  setSessionStorage,
  isAgentLoggedIn,
} from '../client/common/utils/browserUtils';

let globalData = {};
let subPageName = '';
export const PDT_REQUEST_ID = 'meta_req_id_ls';

function daysFromToday(epochMs) {
  const today = new Date().getTime();
  return Math.floor((epochMs - today) / (1000 * 60 * 60 * 24));
}

export const setSubPageName = pageName => {
  subPageName = pageName;
};

export const getSubPageName = () => subPageName;

export const getGlobalData = () => globalData;

const getPreviousPageName = referrer => {
  if (!referrer) return '';

  const url = new URL(referrer);
  const path = url.pathname;

  switch (true) {
    // LANDING route
    case path.includes('/holidays-india/'):
      return PAGE_NAMES.LANDING;

    // LISTING routes
    case path.includes('/holidays/india/search'):
    case path.includes('/holidays/international/search'):
      return PAGE_NAMES.LISTING;

    // DETAILS routes
    case path.includes('/holidays/india/package'):
    case path.includes('/holidays/international/package'):
    case path.includes('/holidays/india/savePackage'):
    case path.includes('/holidays/india/diyPackageDetail'):
    case path.includes('/holidays/previewDetail'):
      return PAGE_NAMES.DETAILS;

    // REVIEW routes
    case path.includes('/holidays/reArchBookingReviewAndPaymentAction'):
    case path.includes('/holidays/reArchBookingReviewAndPaymentAction!openReviewPage'):
      return PAGE_NAMES.REVIEW;

    // PSM DETAILS route
    case path.includes('/holidays/psm/quotes/detail'):
      return PAGE_NAMES.PSM_DETAILS;

    // PSM LISTING route
    case path.includes('/holidays/psm/quotes/listing'):
      return PAGE_NAMES.PSM;

    // DIY routes
    case path.includes('/holidays/india/planner'):
    case path.includes('/holidays/diyPlanner'):
      return PAGE_NAMES.DIY;

    // THANK YOU route
    case path.includes('/holidays/onlineBookingPaymentThankyouAction'):
      return PAGE_NAMES.THANK_YOU;

    // SEND QUERY route
    case path.includes('/holidays/sendQuery30!packageSendQueryForm'):
      return PAGE_NAMES.SEND_QUERY;

    default:
      return PAGE_NAMES.EXTERNAL;
  }
};

export const DEFAULT_SEARCH_CONTEXT = {
  to: {
    locus: {
      locus_id: '',
      locus_type: '',
      country: '',
      locus_v2_id: '',
      locus_v2_type: '',
    },
    locus_v2: {
      locus_id: '',
      locus_type: '',
      country: '',
    },
    location: {
      code: '',
      name: '',
      type: '',
      country: '',
      country_code: '',
    },
  },
  from: {
    locus: {
      locus_id: '',
      locus_type: '',
      country: '',
      locus_v2_id: '',
      locus_v2_type: '',
    },
    locus_v2: {
      locus_id: '',
      locus_type: '',
      country: '',
    },
    location: {
      code: '',
      name: '',
      type: '',
      country: '',
      country_code: '',
    },
  },
};

export const setGlobalData = ({
  departureDetails,
  destinationDetails,
  paxDetails,
  lob_category,
  departureDate,
  to_date_time,
  tags,
  error,
  event_detail,
  pageContextPageType,
  product_id,
  user_context,
  adobe_analytics,
}) => {
  const previousPage = getPreviousPageName(document.referrer);
  const search_context = DEFAULT_SEARCH_CONTEXT;
  let error_details_list = [];
  // search_context.search_type = 'search_type';
  // search_context.travel_class = 'travel_class';
  if (destinationDetails) {
    search_context.to = { ...destinationDetails };
  }
  if (to_date_time) {
    search_context.to_date_time = to_date_time;
  }
  if (departureDate) {
    search_context.from_date_time = departureDate;
    search_context.advance_purchase = daysFromToday(departureDate);
  }
  if (tags) {
    search_context.tags = tags?.split('~')?.map(tag => ({
      tagItem: {
        type: 'category',
        value: tag,
      },
    }));
  }
  if (product_id) {
    search_context.product_id = String(product_id);
  }
  if (departureDetails) {
    search_context.from = { ...departureDetails };
  }
  if (paxDetails) {
    const { noOfAdults, noOfInfants, noOfChildrenWOB, ageOfChildrenWOB, rooms } = paxDetails;
    search_context.pax = {
      count: noOfAdults + noOfInfants + noOfChildrenWOB,
      rooms,
      details: {
        adult: {
          count: noOfAdults,
        },
        ...(noOfInfants > 0 && {
          infants: {
            count: noOfInfants,
          },
        }),
        ...(noOfChildrenWOB > 0 &&
          ageOfChildrenWOB && {
            child: {
              ages: ageOfChildrenWOB,
              count: noOfChildrenWOB,
            },
          }),
      },
    };
  }
  if (adobe_analytics) {
    globalData.adobe_analytics = {
      ...adobe_analytics,
    };
  }
  if (error) {
    error_details_list = [{ message: error }];
  }
  globalData = {
    search_context,
    lob_category: lob_category === 'DOM' ? 'domhld' : 'intlhld',
    previous_page: previousPage,
    experiment_details: JSON.parse(getSessionStorage(MMT_POKUS_VAR) || null),
    event_detail,
    pageContextPageType,
    user_context: {
      user_type: isAgentLoggedIn() ? 'AGENT' : 'PERSONAL',
      ...(user_context || {}),
    },
    adobe_analytics,
  };

  if (error_details_list.length > 0) globalData.error_details_list = error_details_list;
};

export const getUserContext = async () => {
  const authToken = getAuthToken();
  if (!authToken) {
    return {};
  }
  try {
    const userInfo = await getUserInfoPIIClient();
    if (userInfo) {
      return {
        is_logged_in: true,
        marketing_cloud_id: window.s.marketingCloudVisitorID,
        profile_type: userInfo.profileType,
        loyalty_status: userInfo.loyalty_status,
        uuid: userInfo.uuid,
        login_channel: userInfo.login_channel,
        user_segment_list: userInfo.hydra_segment_list,
        user_type: isAgentLoggedIn() ? 'AGENT' : 'PERSONAL',
      };
    }
    throw new Error('Error in getting user info');
  } catch (e) {
    console.error('Error in getting user info', e);
    return {};
  }
};

export const getDeviceContext = async () => {
  try {
    return {
      build_ver: '1001',
    };
  } catch (e) {
    console.error('Error in getting device info', e);
    return {};
  }
};

export const generateJourneyId = () => {
  const journeyId = uuid();

  if (!getMMT_HLD_OBJ().fromSEO) {
    window.journeyId = journeyId;
  }
  return journeyId;
};

export const getJourneyId = () => {
  const MMT_HLD = getMMT_HLD_OBJ();
  return MMT_HLD.fromSEO ? MMT_HLD.journeyId : (window?.journeyId ?? generateJourneyId());
};

export const getRequestId = () => uuid();

export const getPdtRequestId = () => {
  const id = getSessionStorage(PDT_REQUEST_ID);
  if (id) return id;
  return updatePdtRequestId();
};

export const updatePdtRequestId = () => {
  const newId = uuid();
  setSessionStorage(PDT_REQUEST_ID, newId);
  return newId;
};
const getLocus = data => ({
  locus_id: data?.locusCode || '',
  locus_type: data?.locusType || '',
  country: data?.countryName || '',
  locus_v2_id: data?.locusCode || '',
  locus_v2_type: data?.locusType || '',
});
const getLocation = data => ({
  code: data?.code || '',
  name: data?.name || '',
  type: data?.type || '',
  country: data?.country || '',
  country_code: data?.countryCode || data?.country_code || '',
});

export const getLocationNodeForPdt = (locationData = {}) => ({
  locus: getLocus(locationData?.locusDetails),
  locus_v2: getLocus(locationData?.locusDetailsV2),
  location: getLocation(locationData?.locationDetails),
});

// In src/analytics/analytics-utils.js
export const generateComponentsL1 = contentDetails => {
  if (!contentDetails || !Array.isArray(contentDetails)) {
    return '';
  }

  const components = contentDetails.map(item => {
    const type = item.type || '';
    const sectionCode = item.section_code || '';
    const name = item.name || '';
    const position = item.position?.v || '';

    // Format: type:section_code:name:V_position
    const result = `${type}:${sectionCode}:${name}:V_${position}`;

    return result;
  });

  const finalResult = `<${components.join('|')}>`;

  return finalResult;
};

export const generateErrorDetailsV11 = errorDetailsList => {
  if (!errorDetailsList || !Array.isArray(errorDetailsList) || errorDetailsList.length === 0) {
    return '';
  }

  const errorStrings = errorDetailsList.map((error, index) => {
    const errorCode = `ERROR_${index + 1}`;
    const errorMessage = error.message;
    return `${errorCode}:${errorMessage}`;
  });

  return `<${errorStrings.join('|')}>`;
};

export const removeEmpty = obj => {
  if (Array.isArray(obj)) {
    return obj.map(item => removeEmpty(item)).filter(item => item !== undefined);
  }
  if (obj !== null && typeof obj === 'object') {
    const cleanedObj = {};
    Object.keys(obj).forEach(key => {
      const value = removeEmpty(obj[key]);
      if (
        value !== null &&
        value !== '' &&
        value !== undefined &&
        !(typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        cleanedObj[key] = value;
      }
    });
    return cleanedObj;
  }
  return obj;
};
